import { useTranslation } from 'react-i18next';
import { ArticleDraftResponse, ArticleResponse } from '../../../api/queries';
import classes from './ArticlesListItem.module.css';
import { Button } from '../../Button';
import { Heading } from '../../Heading';
import { PillLabel } from '../../PillLabel/PillLabel';
import { useNavigate } from 'react-router-dom';
import { useCheckGetDraftArticleExistsByGuideIdMutation } from '../../../hooks/mutations/useCheckGetDraftArticleExistsByGuideIdMutation';
import { useCreateDraftMutation } from '../../../hooks/mutations/useCreateDraftMutation';
import { TrashFillIcon } from '@navikt/aksel-icons';
import { useDeleteArticleMutation } from '../../../hooks/mutations/useDeleteArticleMutation';
import { toast } from 'react-toastify';
import { useDeleteDraftArticleMutation } from '../../../hooks/mutations/useDeleteDraftArticleMutation';
import { Dialog } from '../../Dialog';
import { DeleteConfirmationDialog } from '../../DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useState } from 'react';

type ArticlesListItemProps = {
  article: ArticleDraftResponse | ArticleResponse;
  type: 'draft' | 'published';
  isContentCreator: boolean;
};

export const ArticlesListItem = ({
  article,
  type,
  isContentCreator,
}: ArticlesListItemProps) => {
  const { t } = useTranslation();
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const { mutateAsync: checkDraft } =
    useCheckGetDraftArticleExistsByGuideIdMutation();
  const { mutate: deleteArticle, isPending: isArticleDeleting } =
    useDeleteArticleMutation();
  const { mutate: deleteDraftArticle, isPending: isDraftDeleting } =
    useDeleteDraftArticleMutation();
  const { mutate: createDraft } = useCreateDraftMutation();

  const onHandleEditArticle = async () => {
    const { exists, draftId } = await checkDraft(
      'guideId' in article ? article.guideId : article.id,
    );
    if (exists) {
      navigate(`/content-system/articles/edit/${draftId}`);
      return;
    }
    const { id, createdByUsers, ...existingArticle } = article;
    createDraft(
      {
        article: {
          ...existingArticle,
          guideId: 'guideId' in article ? article.guideId : article.id,
          draftId: 0,
          updatedAt: new Date(),
        },
      },
      {
        onSuccess: (res) => {
          navigate(`/content-system/articles/edit/${res.id}`);
        },
      },
    );
  };

  const toastDeletedMessage = () =>
    toast.success(t('articlesList.deleteSuccessToast', { title: article.title }), {
      position: 'bottom-right',
    });

  const onHandleDeleteArticle = () => {
    if (type === 'published') {
      deleteArticle(
        { id: article.id },
        {
          onSuccess: () => toastDeletedMessage(),
        },
      );
      return;
    }

    deleteDraftArticle(
      { draftId: article.id },
      {
        onSuccess: () => toastDeletedMessage(),
      },
    );
  };

  return (
    <div className={classes.articleCard}>
      <div className={classes.content}>
        <div className={classes.frontImage}>
          {article.coverImageUrl && (
            <img
              src={article.coverImageUrl}
              alt={article.coverImageAlt ?? article.title}
            />
          )}
        </div>
        <Heading as="h3">{article.title}</Heading>
        <div className={classes.labels}>
          {article.labels.map((label) => (
            <PillLabel label={label} key={label.id} />
          ))}
        </div>
      </div>
      <div className={classes.buttons}>
        {type === 'published' && (
          <Button
            variant="secondary"
            className={classes.readMoreButton}
            onClick={() => navigate(`/content-system/articles/${article.id}`)}
          >
            {t('articlesList.read')}
          </Button>
        )}

        {isContentCreator && (
          <>
            <Button
              variant="primary"
              className={classes.editButton}
              onClick={onHandleEditArticle}
            >
              {t('articlesList.edit')}
            </Button>
            <Button
              variant="neutral"
              className={classes.deleteButton}
              aria-label={t('articlesList.deleteArticle')}
              onClick={() => setIsDeleteOpen(true)}
            >
              <TrashFillIcon aria-hidden="true" fontSize={'1.2rem'} />
            </Button>
          </>
        )}

        <Dialog
          isOpen={isDeleteOpen}
          closeDialog={() => setIsDeleteOpen(false)}
        >
          <DeleteConfirmationDialog
            setOpenDeleteConfirmationDialog={setIsDeleteOpen}
            onDeleteConfirmationDialog={onHandleDeleteArticle}
            title={t('articlesList.deleteArticleTitle')}
            text={
              <span
                dangerouslySetInnerHTML={{
                  __html: t('articlesList.deleteConfirmationText'),
                }}
              />
            }
            isDeleting={
              type === 'published' ? isArticleDeleting : isDraftDeleting
            }
          />
        </Dialog>
      </div>
    </div>
  );
};
